///
/// EarthBrowser
///
/// useBuoyObservations Hook
///
/// RTK Query hook for fetching historical buoy observations
///
/// Created by Matt Giger on December 21, 2025
/// Copyright (c) 2025 Geodata Labs
///

import { useGetBuoyHistoryQuery } from '@/redux/earthBrowserApi'
import type { BuoyHistoryResponse } from '@shared-types/buoy'

interface UseBuoyObservationsOptions {
	stationId: string
	hours?: number // 1-168, default 24
}

interface UseBuoyObservationsResult {
	data: BuoyHistoryResponse | undefined
	isLoading: boolean
	isError: boolean
	error: unknown
	refetch: () => void
}

/**
 * Fetch historical observations for a buoy station
 *
 * @param options.stationId - Station ID (e.g., "46042")
 * @param options.hours - Hours of history to fetch (1-168, default 24)
 *
 * @example
 * ```tsx
 * const { data, isLoading, isError } = useBuoyObservations({
 *   stationId: '46042',
 *   hours: 72  // 3 days
 * })
 *
 * if (isLoading) return <div>Loading...</div>
 * if (isError) return <div>Error loading data</div>
 *
 * return (
 *   <div>
 *     <h2>{data.station.name}</h2>
 *     <p>{data.observations.length} observations</p>
 *   </div>
 * )
 * ```
 */
export function useBuoyObservations(
	options: UseBuoyObservationsOptions
): UseBuoyObservationsResult {
	const { stationId, hours = 24 } = options

	const { data, isLoading, isError, error, refetch } = useGetBuoyHistoryQuery(
		{
			stationId,
			hours
		},
		{
			skip: !stationId, // Skip query if no stationId
		}
	)

	return {
		data,
		isLoading,
		isError,
		error,
		refetch: () => { void refetch() }
	}
}

/**
 * Helper to calculate time range selector options
 */
export const TIME_RANGES = {
	'6h': { hours: 6, label: '6 Hours' },
	'12h': { hours: 12, label: '12 Hours' },
	'24h': { hours: 24, label: '24 Hours' },
	'3d': { hours: 72, label: '3 Days' },
	'7d': { hours: 168, label: '7 Days' }
} as const

export type TimeRangeKey = keyof typeof TIME_RANGES
